import { z } from 'zod'

/**
 * Ob eine Domain von außen erreichbar ist — gemessen vom Kontor-Backend aus, nicht auf dem
 * Server selbst. Genau das ist der Punkt: Ein Dienst kann auf dem Server tadellos laufen und
 * trotzdem für die halbe Welt tot sein, weil DNS falsch zeigt oder eine Firewall dazwischen
 * steht. Diese Prüfung braucht deshalb bewusst keine SSH-Verbindung und funktioniert auch dann
 * noch, wenn der Server gar nicht mehr antwortet.
 */
export const reachabilityStatusSchema = z.enum(['ok', 'warn', 'error', 'skipped'])
export type ReachabilityStatus = z.infer<typeof reachabilityStatusSchema>

export const reachabilityCheckSchema = z.object({
  /** Stabiler Schlüssel, damit die Oberfläche Symbole zuordnen kann, ohne Text zu vergleichen. */
  key: z.enum(['dns', 'tcp-80', 'tcp-443', 'http', 'https', 'redirect', 'certificate']),
  title: z.string(),
  status: reachabilityStatusSchema,
  detail: z.string(),
  /** Was zu tun wäre — nur gesetzt, wenn es etwas zu tun gibt. */
  hint: z.string().nullable(),
  /** Wie lange die Messung gedauert hat; null, wenn nichts gemessen wurde. */
  durationMs: z.number().int().nonnegative().nullable(),
})
export type ReachabilityCheck = z.infer<typeof reachabilityCheckSchema>

export const reachabilityReportSchema = z.object({
  host: z.string(),
  checkedAt: z.iso.datetime(),
  checks: z.array(reachabilityCheckSchema),
  /** Das schlechteste Einzelergebnis, damit die Kopfzeile ohne Nachrechnen auskommt. */
  worst: z.enum(['ok', 'warn', 'error']),
})
export type ReachabilityReport = z.infer<typeof reachabilityReportSchema>

/**
 * Ein Hostname, wie ihn ein Besucher eintippt. Bewusst ohne Schema und ohne Pfad: geprüft wird
 * die Erreichbarkeit einer Adresse, nicht einer einzelnen Seite.
 */
export const reachabilityQuerySchema = z.object({
  host: z
    .string()
    .trim()
    .min(1, 'Bitte gib eine Domain an.')
    .max(253, 'Das ist kein Hostname.')
    .refine(
      (value) => !value.includes('/') && !value.includes(' ') && !value.includes(':'),
      'Bitte nur den Hostnamen, ohne https:// und ohne Pfad.',
    ),
  /** Optional: Gegen welchen Server die DNS-Antwort geprüft wird. */
  serverId: z.string().min(1).optional(),
})
